import useSafeApiKit from 'src/common/api-clients/safe-protocol-kit/useSafeApiKit'
import useProtocolKit from 'src/common/api-clients/safe-protocol-kit/useSafeProtocolKit'
import useSmartAccount from 'src/common/api-clients/safe-protocol-kit/useSmartAccount'
import { big } from 'src/common/utils/big-utils'
import { resolveAllThirdwebTransactions } from 'src/common/utils/web3/resolveThirdwebTransaction'
import { useQuery } from '@tanstack/react-query'
import { useCallback } from 'react'
import { PreparedTransaction } from 'thirdweb'
import { useEstimateGasCost } from 'thirdweb/react'

interface UseEstimateGasOptions {
  transaction?: PreparedTransaction | PreparedTransaction[]
}

const useGasEstimate = ({ transaction }: UseEstimateGasOptions) => {
  const { smartAccountInfo, isLoading: smartAccountLoading } = useSmartAccount()
  const { protocolKit, isLoading: protocolKitLoading } = useProtocolKit()
  const apiKit = useSafeApiKit()

  const { mutateAsync: estimateGasCost } = useEstimateGasCost()

  const {
    data: gasEstimateInEther,
    refetch,
    isLoading,
    ...rest
  } = useQuery({
    queryKey: ['gasEstimate'],
    queryFn: async () => {
      if (!transaction) return null
      if (Array.isArray(transaction)) {
        if (!protocolKit || !smartAccountInfo?.address) return

        const transactions = await resolveAllThirdwebTransactions(transaction)
        const txBatch = await protocolKit.createTransaction({
          transactions: transactions
        })
        const { safeTxGas } = await apiKit.estimateSafeTransaction(
          smartAccountInfo?.address,
          {
            ...txBatch.data,
          }
        )

        return big(safeTxGas)
      }

      const gasCost = big((await estimateGasCost(transaction)).ether)
      return gasCost
    },
    staleTime: 20000,
    enabled: !!transaction, // Only run if a transaction is provided
    refetchOnMount: true,
    refetchOnWindowFocus: true, // Avoid refetching when the window regains focus
  })

  const estimateGas = useCallback(() => refetch(), [refetch])

  return {
    gasEstimateInEther: gasEstimateInEther ?? null,
    estimateGas,
    loading: isLoading || smartAccountLoading || protocolKitLoading,
    ...rest,
  }
}

export default useGasEstimate
